import { CONTENT_HUB_SOURCES, type ContentHubSource } from "./config";
import type {
  ContentHubItem,
  ContentHubSearchHit,
  ContentHubStreamFrame,
  FinXPayload,
  HeadlinesPayload,
  StocksNewsPayload,
  TruthSocialPayload,
} from "./types";

const asString = (value: unknown): string | null =>
  typeof value === "string" ? value : null;

/** Normalized display fields extracted from a source-specific payload. */
type PayloadFields = {
  title: string;
  body: string | null;
  occursAt: string;
  tickers: string[];
};

const headlinesFields = (raw: Record<string, unknown>): PayloadFields => {
  const payload = raw as Partial<HeadlinesPayload>;
  return {
    title: payload.title ?? "",
    body: payload.body ?? null,
    occursAt: payload.occurs_at ?? "",
    tickers: payload.tickers ?? [],
  };
};

const truthSocialFields = (raw: Record<string, unknown>): PayloadFields => {
  const payload = raw as Partial<TruthSocialPayload>;
  return {
    title: payload.content ?? "",
    body: null,
    occursAt: payload.created_at ?? "",
    tickers: [],
  };
};

const finXFields = (raw: Record<string, unknown>): PayloadFields => {
  const payload = raw as Partial<FinXPayload>;
  return {
    title: payload.text ?? "",
    body: null,
    occursAt: payload.created_at ?? "",
    tickers: [],
  };
};

const stocksNewsFields = (raw: Record<string, unknown>): PayloadFields => {
  const payload = raw as Partial<StocksNewsPayload>;
  return {
    title: payload.title ?? "",
    body: payload.body ?? null,
    occursAt: payload.created ?? "",
    tickers: payload.stocks ?? [],
  };
};

/** Fallback for sources without a declared payload schema yet. */
const defaultFields = (raw: Record<string, unknown>): PayloadFields => ({
  title: asString(raw.title) ?? "",
  body: asString(raw.body),
  occursAt: asString(raw.created_at) ?? "",
  tickers: [],
});

/** Per-source payload schema mappers. Add an entry here when integrating a
 *  new source; sources without one fall back to defaultFields. */
const payloadFieldMappers: Record<
  ContentHubSource,
  (raw: Record<string, unknown>) => PayloadFields
> = {
  [CONTENT_HUB_SOURCES.HEADLINES]: headlinesFields,
  [CONTENT_HUB_SOURCES.TRUTH_SOCIAL]: truthSocialFields,
  [CONTENT_HUB_SOURCES.FIN_X]: finXFields,
  [CONTENT_HUB_SOURCES.STOCKS_ONE]: stocksNewsFields,
  [CONTENT_HUB_SOURCES.STOCKS_TWO]: stocksNewsFields,
  [CONTENT_HUB_SOURCES.STOCKS_THREE]: stocksNewsFields,
  [CONTENT_HUB_SOURCES.STOCKS_FOUR]: stocksNewsFields,
  [CONTENT_HUB_SOURCES.STOCKS_FOUR_FILINGS]: stocksNewsFields,
};

function fromPayload(
  backendSource: string,
  payload: Record<string, unknown>
): PayloadFields {
  const mapper =
    payloadFieldMappers[backendSource as ContentHubSource] ?? defaultFields;
  return mapper(payload);
}

/** Map a POST /v1/search hit into the normalized model. Search hits have no
 *  publish_id — it's constructed from backend_source + source_id. Envelope
 *  fields (occurs_at, tickers, text_1) fill in where present. */
export function mapSearchHit(hit: ContentHubSearchHit): ContentHubItem {
  const payload = hit.payload ?? {};
  const fields = fromPayload(hit.backend_source, payload);
  return {
    publishId: `${hit.backend_source}:${hit.source_id}`,
    backendSource: hit.backend_source,
    sourceId: hit.source_id,
    sourceType: hit.source_type,
    title: fields.title || (hit.text_1 ?? ""),
    body: fields.body,
    // Prefer the envelope time, then the payload time; fall back to
    // indexed_at so every item carries a parseable timestamp even when a
    // source omits its own (e.g. fin-x has a null envelope occurs_at).
    occursAt: hit.occurs_at || fields.occursAt || hit.indexed_at,
    tickers: hit.tickers && hit.tickers.length > 0 ? hit.tickers : fields.tickers,
    raw: payload,
  };
}

/** Map a GET /v1/stream frame into the normalized model. The frame envelope
 *  is thinner than a search hit — the payload is the authoritative content. */
export function mapStreamFrame(frame: ContentHubStreamFrame): ContentHubItem {
  const payload = frame.payload ?? {};
  const fields = fromPayload(frame.backend_source, payload);
  return {
    publishId:
      frame.publish_id || `${frame.backend_source}:${frame.source_id}`,
    backendSource: frame.backend_source,
    sourceId: frame.source_id,
    sourceType: frame.source_type ?? "",
    title: fields.title,
    body: fields.body,
    occursAt: fields.occursAt,
    tickers: fields.tickers,
    raw: payload,
  };
}
